import React, { useContext, useEffect, useMemo, useState } from 'react';
import ContentLoader from 'react-content-loader';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import { useLoaderData, useParams } from 'react-router-dom';

import Descriptor from '@r6digital/rapidstor-types/react/entities/Descriptor';
import Location from '@r6digital/rapidstor-types/react/entities/Location';
import UnitType from '@r6digital/rapidstor-types/react/entities/UnitType';
import { MovedInReservation } from '@r6digital/rapidstor-types/types/Reservation';

import {
    useGetCorpQuery,
    useGetReservationQuery,
} from '@/api/rapidstor';
import Logger from '@/utils/logging';

import store from '@/store';
import { setUnitData } from '@/store/bookingSlice';

import Alert from '@/components/elements/alert';
import Button from '@/components/elements/button';
import WizardDescriptor from '@/components/rapidstor/wizard/descriptor';
import MarketingSurvey from '@/components/rapidstor/wizard/forms/marketing-survey';

import { JourneyContext } from '@/features/rapidstor/providers/JourneyProvider';

import classes from '../scss/screens/move-in.module.scss';

export default function MoveInConfirmed() {
    const location = useLoaderData() as Location;
    const { token } = useParams();
    const { t } = useTranslation();
    const { unitList } = useContext(JourneyContext);

    const [descriptor, setDescriptor] = useState<Descriptor>();
    const [unitType, setUnitType] = useState<UnitType>();
    const [reservation, setReservation] = useState<MovedInReservation>();
    const [copied, setCopied] = useState<boolean>(false);

    const { data: reservationData, isLoading: reservationDataLoading } =
        useGetReservationQuery(
            {
                locationCode: location.locationCode,
                corpCode: location.corpCode,
                token: token,
            },
            {
                refetchOnMountOrArgChange: true,
            }
        );

    const { data: corporationData } = useGetCorpQuery(
        {
            corpCode: location.corpCode,
        },
        {
            refetchOnMountOrArgChange: false,
        }
    );

    const [, setCookie, removeCookie] = useCookies(['reservation', 'sessionId']);

    const copyToClipboard = () => {
        if (reservation?.globalWaitingNum) {
            navigator.clipboard.writeText(`${reservation.globalWaitingNum}`);
            setCopied(true);
        }
    };

    const errorAfterMoveIn = useMemo(() => {
        return reservation?.ledgerResult?.errorAfterMoveIn || ''
    }, [reservation]);

    const handleAfterMoveInErrorMessage = () => {
        if (!errorAfterMoveIn) {
            return '';
        }

        switch (true) {
            case reservation?.ledgerResult?.failedAutobillSetup:
                return t(`wizard:bookingConfirmation:autobillError`);
            case reservation?.ledgerResult?.failedMerchPayment:
                return t(`wizard:bookingConfirmation:merchPaymentError`);
            case errorAfterMoveIn === 'Unable to assign insurance policy number':
                return t(`wizard:bookingConfirmation:insuranceError`);
            default:
                return errorAfterMoveIn;
        }
    };

    const clipBoardIcon = useMemo(() => {
        if (copied) {
            return (
                <svg
                    xmlns='http://www.w3.org/2000/svg'
                    fill='none'
                    viewBox='0 0 24 24'
                    strokeWidth={1.5}
                    stroke='currentColor'
                >
                    <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        d='M11.35 3.836c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m8.9-4.414c.376.023.75.05 1.124.08 1.131.094 1.976 1.057 1.976 2.192V16.5A2.25 2.25 0 0 1 18 18.75h-2.25m-7.5-10.5H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V18.75m-7.5-10.5h6.375c.621 0 1.125.504 1.125 1.125v9.375m-8.25-3 1.5 1.5 3-3.75'
                    />
                </svg>
            );
        } else {
            return (
                <svg
                    xmlns='http://www.w3.org/2000/svg'
                    fill='none'
                    viewBox='0 0 24 24'
                    strokeWidth={1.5}
                    stroke='currentColor'
                >
                    <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        d='M15.666 3.888A2.25 2.25 0 0 0 13.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 0 1-.75.75H9a.75.75 0 0 1-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 0 1-2.25 2.25H6.75A2.25 2.25 0 0 1 4.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 0 1 1.927-.184'
                    />
                </svg>
            );
        }
    }, [copied]);

    useEffect(() => {
        const setupSliceData = () => {
            // Set UnitData
            if (descriptor && unitType && unitList) {
                store.dispatch(
                    setUnitData({
                        location,
                        unitType: unitType,
                        descriptor,
                        descriptors: unitList.descriptors,
                    })
                );
            }
        };

        const checkCookie = () => {
            if (reservation) {
                // Set Cookie
                setCookie(
                    'reservation',
                    {
                        token: token,
                        id: reservation._id,
                    },
                    {
                        path: window.location.pathname,
                        maxAge: 60 * 60 * 24 * 7,
                    }
                );
                setupSliceData();
            }
        };

        if (reservationData && unitList) {
            /** Esign Redirect */
            if ('finalized' in reservationData && reservationData.finalized) {
                // remove cookie so a user could come back and do another reservation properly
                removeCookie('reservation');
                removeCookie('sessionId');
                if (location.rapidstor.autoESignRedirect && reservationData.ledgerResult?.esign?.url) {
                    window.location.href = reservationData.ledgerResult.esign.url;
                    return;
                }
            }

            unitList.descriptors.map((descriptor) => {
                const found = descriptor.unitTypes.find((uT: UnitType) => {
                    return uT._id === reservationData.unitType._id;
                });
                if (found) {
                    setReservation(reservationData as MovedInReservation);
                    if (reservationData.persona) {
                        setUnitType({
                            ...found,
                            rates: {
                                ...found.rates,
                                rate: reservationData.rates.rate,
                                discounted: reservationData.rates.discounted,
                                discountedRate: reservationData.rates.discountedRate ?? 0,
                            },
                        });
                        setDescriptor({
                            ...descriptor,
                            name: reservationData.unitType.descriptor.name,
                            tags: reservationData.unitType.descriptor.tags,
                            description: reservationData.unitType.descriptor.description,
                            descriptions:
                                reservationData.unitType.descriptor.descriptions,
                            specialText: reservationData.unitType.descriptor.specialText,
                        });
                    } else {
                        setUnitType(found);
                        setDescriptor(descriptor);
                    }
                    checkCookie();
                }
            });
        }
    }, [
        unitList,
        reservationData,
        reservation,
        setCookie,
        removeCookie,
        token,
        descriptor,
        unitType,
        location,
    ]);

    useEffect(() => {
        Logger.info('MoveInConfirmed Screen Loaded', location, [
            {
                name: 'wizard_test',
                value: true,
            },
        ]);
    }, [location]);

    const navigateToIdVerify = () => {
        // TODO: if we ever need to use a URL that's not from MTAS, we'll need to somehow be able to configure the URL and queries (if neede)
        window.location.href = `${window.rapidstor_settings.app_config.journey.idVerify?.url}?email=${reservation?.email}&tenantId=${reservation?.tenantId}`;
    }

    return (
        <div className={`${classes.container}`}>
            <div className={`${classes.header}`}>
                <span className={`${classes.title}`} style={{ flexGrow: '1' }}>
                    {t('wizard:scheduleMoveIn')}
                </span>
            </div>

            <WizardDescriptor
                descriptor={descriptor}
                unitType={unitType}
                deal={reservation?.deal}
            />

            <div className={`${classes.fold}`}></div>

            <div className={`${classes.booking_confirmation}`}>
                {reservationDataLoading ? (
                    <ContentLoader
                        foregroundColor={`${location.skin.mainColour}`}
                        backgroundColor='#fff'
                        height={'156px'}
                        width={'100%'}
                    >
                        <rect x='0' y='0' rx='5' ry='5' width='100%' height='65px' />
                        <rect x='0' y='95' rx='5' ry='5' width='100%' height='25px' />
                        <rect x='0' y='130' rx='5' ry='5' width='100%' height='25px' />
                    </ContentLoader>
                ) : (
                    <>
                        <div className={`${classes.title}`}>
                            <svg
                                xmlns='http://www.w3.org/2000/svg'
                                fill='none'
                                viewBox='0 0 24 24'
                                strokeWidth={1.5}
                                stroke='currentColor'
                                className={`${classes.thumbs_up}`}
                            >
                                <path
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                    d='M6.633 10.25c.806 0 1.533-.446 2.031-1.08a9.041 9.041 0 0 1 2.861-2.4c.723-.384 1.35-.956 1.653-1.715a4.498 4.498 0 0 0 .322-1.672V2.75a.75.75 0 0 1 .75-.75 2.25 2.25 0 0 1 2.25 2.25c0 1.152-.26 2.243-.723 3.218-.266.558.107 1.282.725 1.282m0 0h3.126c1.026 0 1.945.694 2.054 1.715.045.422.068.85.068 1.285a11.95 11.95 0 0 1-2.649 7.521c-.388.482-.987.729-1.605.729H13.48c-.483 0-.964-.078-1.423-.23l-3.114-1.04a4.501 4.501 0 0 0-1.423-.23H5.904m10.598-9.75H14.25M5.904 18.5c.083.205.173.405.27.602.197.4-.078.898-.523.898h-.908c-.889 0-1.713-.518-1.972-1.368a12 12 0 0 1-.521-3.507c0-1.553.295-3.036.831-4.398C3.387 9.953 4.167 9.5 5 9.5h1.053c.472 0 .745.556.5.96a8.958 8.958 0 0 0-1.302 4.665c0 1.194.232 2.333.654 3.375Z'
                                />
                            </svg>

                            <span>{t('wizard:bookingConfirmation:title')}</span>
                        </div>
                        {t('wizard:bookingConfirmation:pleaseWait', { defaultValue: '' }) && (
                            <p className={`${classes.pleaseWait}`}>
                                {t('wizard:bookingConfirmation:pleaseWait')}
                            </p>
                        )}
                        <p className={`${classes.subtitle}`}>
                            {t('wizard:bookingConfirmation:subtitle')}
                        </p>

                        { errorAfterMoveIn && (
                            <Alert type={'warning'} text={handleAfterMoveInErrorMessage()} />
                        )}
                        <div
                            className={`${classes.referenceCode}`}
                            onClick={copyToClipboard}
                        >
                            <span className={`${classes.text}`}>
                                {t('wizard:bookingConfirmation:referenceCode')}
                            </span>
                            <span className={`${classes.code}`}>
                                {reservation?.globalWaitingNum}
                            </span>
                            {clipBoardIcon}
                        </div>

                        {window.rapidstor_settings.app_config.journey.idVerify?.url && (
                            <div
                                className={`${classes.id_verify_section}`}
                            >
                                <p>{t('wizard:bookingConfirmation:idVerifyText')}</p>
                                <Button
                                    size='small'
                                    type={'primary'}
                                    onclick={navigateToIdVerify}>{t('wizard:bookingConfirmation:idVerifyButtonText')}
                                </Button>
                            </div>
                        )}
                    </>
                )}
            </div>

            <div className={`${classes.customer_survey}`}>
                {corporationData?.settings.storApp &&
                    corporationData?.settings.storApp.enabled && (
                        <>
                            <div className={`${classes.fold}`}></div>
                            <span
                                className={`${classes.app_download_text}`}
                                dangerouslySetInnerHTML={{
                                    __html: `${corporationData?.settings.storApp.blurbText}`,
                                }}
                            ></span>

                            <div className={`${classes.store_images}`}>
                                <img
                                    src={`https://ik.imagekit.io/bytcx9plm/PlayStore.png?updatedAt=1726538026462`}
                                    onClick={() => {
                                        window.open(
                                            corporationData?.settings.storApp.links
                                                .googlePlay,
                                            '_blank'
                                        );
                                    }}
                                />
                                <img
                                    src={`https://ik.imagekit.io/bytcx9plm/AppleStore.png?updatedAt=1726538026336`}
                                    onClick={() => {
                                        window.open(
                                            corporationData?.settings.storApp.links
                                                .iosAppStore,
                                            '_blank'
                                        );
                                    }}
                                />
                            </div>
                        </>
                    )}

                {(location.settings.marketingSurvey.use && reservation?.tenantId) && (
                    <>
                        <div className={`${classes.fold}`}></div>
                        <MarketingSurvey
                            marketingSurvey={location.settings.marketingSurvey}
                            tenantId={reservation.tenantId}
                        />
                    </>
                )}
            </div>
        </div>
    );
}
